import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useCustomerInfo, CustomerInfoType, NodeSettings } from '../../../shared/hooks/useCustomerInfo';
import { useResponsive } from '../../../shared/hooks/useResponsive';

interface ImmersiveCustomerInfoProps {
  customerInfo: CustomerInfoType;
  setCustomerInfo: React.Dispatch<React.SetStateAction<CustomerInfoType>>;
  onNext: () => void;
  onPrev: () => void;
  nodeData?: { label: string; settings: NodeSettings };
}


const ImmersiveCustomerInfo: React.FC<ImmersiveCustomerInfoProps> = ({
  customerInfo: externalCustomerInfo,
  setCustomerInfo: externalSetCustomerInfo,
  onNext,
  nodeData,
}) => {
  const { isMobile } = useResponsive();

  const requirePhone = nodeData?.settings?.requirePhone ?? true;
  const requireEmail = nodeData?.settings?.requireEmail ?? true;

  const {
    customerInfo,
    errors,
    touched,
    handleChange,
    handlePhoneChange,
    handleBlur,
    isValid,
  } = useCustomerInfo(externalCustomerInfo);

  React.useEffect(() => {
    externalSetCustomerInfo(customerInfo);
  }, [customerInfo, externalSetCustomerInfo]);

  const canProceed = isValid || (
    customerInfo.firstName.trim().length >= 2 &&
    customerInfo.lastName.trim().length >= 2 &&
    (!requireEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) &&
    (!requirePhone || customerInfo.phone.replace(/\D/g, '').length >= 10)
  );

  if (isMobile) {
    return (
      <div style={{ width: '100%', height: '100vh', overflow: 'hidden', background: '#080808', position: 'relative' }}>
        <style>{`
          #lp-cust-m::-webkit-scrollbar { display: none; }
          .lp-imm-input::placeholder { color: rgba(255,255,255,0.18); }
          .lp-imm-input:focus { outline: none; border-color: rgba(255,255,255,0.3) !important; background: rgba(255,255,255,0.07) !important; }
          .lp-imm-input:-webkit-autofill,
          .lp-imm-input:-webkit-autofill:hover,
          .lp-imm-input:-webkit-autofill:focus {
            -webkit-text-fill-color: #fff;
            -webkit-box-shadow: 0 0 0px 1000px #0f0f0f inset;
            transition: background-color 5000s ease-in-out 0s;
          }
          .lp-imm-phone .react-tel-input .form-control {
            background: transparent !important; color: #fff !important; border: none !important;
            height: 52px !important; padding-left: 52px !important; font-size: 15px !important;
            letter-spacing: 0.01em !important; width: 100% !important;
          }
          .lp-imm-phone .react-tel-input .flag-dropdown { background: transparent !important; border: none !important; border-radius: 10px 0 0 10px !important; }
          .lp-imm-phone .react-tel-input .selected-flag { background: transparent !important; border-radius: 10px 0 0 10px !important; padding-left: 14px !important; width: 46px !important; }
          .lp-imm-phone .react-tel-input .selected-flag:hover,
          .lp-imm-phone .react-tel-input .flag-dropdown.open .selected-flag { background: rgba(255,255,255,0.05) !important; }
          .lp-imm-phone .react-tel-input .country-list { background: #111 !important; border: 1px solid rgba(255,255,255,0.12) !important; border-radius: 10px !important; margin-top: 4px !important; color: rgba(255,255,255,0.8) !important; }
          .lp-imm-phone .react-tel-input .country-list .country:hover { background: rgba(255,255,255,0.07) !important; }
          .lp-imm-phone .react-tel-input .country-list .country.highlight { background: rgba(255,255,255,0.1) !important; }
          .lp-imm-phone .react-tel-input .country-list .search { background: #111 !important; border-bottom: 1px solid rgba(255,255,255,0.08) !important; padding: 8px 10px !important; }
          .lp-imm-phone .react-tel-input .country-list .search-box { background: rgba(255,255,255,0.06) !important; border: 1px solid rgba(255,255,255,0.1) !important; border-radius: 6px !important; color: #fff !important; width: 100% !important; margin: 0 !important; }
          .lp-imm-phone .react-tel-input .country-list .search-box::placeholder { color: rgba(255,255,255,0.22) !important; }
          .lp-imm-phone .react-tel-input .country-list .dial-code { color: rgba(255,255,255,0.3) !important; }
        `}</style>


        <div id="lp-cust-m" style={{ height: '100vh', overflowY: 'auto', scrollbarWidth: 'none', padding: '52px 24px 48px' }}>
          <p style={{ color: 'rgba(255,255,255,0.22)', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 36 }}>
            Your details
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <FormField label="First name" name="firstName" value={customerInfo.firstName} error={touched.firstName ? errors.firstName : ''} onChange={handleChange} onBlur={() => handleBlur('firstName')} placeholder="Alex" />
              <FormField label="Last name" name="lastName" value={customerInfo.lastName} error={touched.lastName ? errors.lastName : ''} onChange={handleChange} onBlur={() => handleBlur('lastName')} placeholder="Smith" />
            </div>
            {requireEmail && (
              <FormField label="Email" name="email" type="email" value={customerInfo.email} error={touched.email ? errors.email : ''} onChange={handleChange} onBlur={() => handleBlur('email')} placeholder="alex@example.com" />
            )}
            {requirePhone && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase' }}>Phone</label>
                <div className="lp-imm-phone" style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${touched.phone && errors.phone ? 'rgba(255,90,90,0.45)' : customerInfo.phone && !errors.phone ? 'rgba(255,255,255,0.38)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 10, transition: 'border-color 0.2s', overflow: 'visible' }}>
                  <PhoneInput country="us" enableSearch value={customerInfo.phone} onChange={handlePhoneChange} onBlur={() => handleBlur('phone')}
                    inputStyle={{ background: 'transparent', color: '#fff', border: 'none', height: 52, fontSize: 15, width: '100%' }}
                    buttonStyle={{ background: 'transparent', border: 'none' }}
                    containerStyle={{ width: '100%' }}
                    dropdownStyle={{ zIndex: 9999 }}
                    countryCodeEditable={false}
                    placeholder="+1 555 000 0000"
                  />
                </div>
                <AnimatePresence>
                  {touched.phone && errors.phone && (
                    <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      style={{ color: 'rgba(255,90,90,0.8)', fontSize: 11, letterSpacing: '0.04em', margin: 0 }}>
                      {errors.phone}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
          <motion.button
            onClick={canProceed ? onNext : undefined}
            whileHover={canProceed ? { background: 'rgba(255,255,255,0.14)', scale: 1.02 } : {}}
            whileTap={canProceed ? { scale: 0.98 } : {}}
            style={{ marginTop: 36, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 24px', background: canProceed ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.03)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: `1px solid ${canProceed ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.06)'}`, borderRadius: 2, color: canProceed ? '#fff' : 'rgba(255,255,255,0.2)', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', cursor: canProceed ? 'pointer' : 'default', transition: 'all 0.25s ease' }}
          >
            Continue
            <span style={{ fontSize: 16, color: canProceed ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.12)' }}>→</span>
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100vh',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      background: '#080808',
    }}>
      <style>{`
        .lp-imm-input::placeholder { color: rgba(255,255,255,0.18); }
        .lp-imm-input:focus { outline: none; border-color: rgba(255,255,255,0.3) !important; background: rgba(255,255,255,0.07) !important; }
        .lp-imm-input:-webkit-autofill,
        .lp-imm-input:-webkit-autofill:hover,
        .lp-imm-input:-webkit-autofill:focus {
          -webkit-text-fill-color: #fff;
          -webkit-box-shadow: 0 0 0px 1000px #0f0f0f inset;
          transition: background-color 5000s ease-in-out 0s;
        }
        .lp-imm-phone .react-tel-input .form-control {
          background: transparent !important; color: #fff !important; border: none !important;
          height: 52px !important; padding-left: 52px !important; font-size: 15px !important;
          letter-spacing: 0.01em !important; width: 100% !important;
        }
        .lp-imm-phone .react-tel-input .flag-dropdown { background: transparent !important; border: none !important; border-radius: 10px 0 0 10px !important; }
        .lp-imm-phone .react-tel-input .selected-flag { background: transparent !important; border-radius: 10px 0 0 10px !important; padding-left: 14px !important; width: 46px !important; }
        .lp-imm-phone .react-tel-input .selected-flag:hover,
        .lp-imm-phone .react-tel-input .flag-dropdown.open .selected-flag { background: rgba(255,255,255,0.05) !important; }
        .lp-imm-phone .react-tel-input .country-list { background: #111 !important; border: 1px solid rgba(255,255,255,0.12) !important; border-radius: 10px !important; margin-top: 4px !important; color: rgba(255,255,255,0.8) !important; scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.12) transparent; }
        .lp-imm-phone .react-tel-input .country-list .country:hover { background: rgba(255,255,255,0.07) !important; }
        .lp-imm-phone .react-tel-input .country-list .country.highlight { background: rgba(255,255,255,0.1) !important; }
        .lp-imm-phone .react-tel-input .country-list .search { background: #111 !important; border-bottom: 1px solid rgba(255,255,255,0.08) !important; padding: 8px 10px !important; }
        .lp-imm-phone .react-tel-input .country-list .search-box { background: rgba(255,255,255,0.06) !important; border: 1px solid rgba(255,255,255,0.1) !important; border-radius: 6px !important; color: #fff !important; width: 100% !important; margin: 0 !important; }
        .lp-imm-phone .react-tel-input .country-list .search-box::placeholder { color: rgba(255,255,255,0.22) !important; }
        .lp-imm-phone .react-tel-input .country-list .dial-code { color: rgba(255,255,255,0.3) !important; }
      `}</style>

      {/* ── Background image placeholder (swap src when ready) ── */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'linear-gradient(135deg, #0d0d0d 0%, #141414 50%, #0a0a0a 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }} />

      {/* Gradient overlay — left stays dark for text legibility, right even darker for form */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(105deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.72) 52%, rgba(0,0,0,0.88) 100%)',
      }} />

      {/* ── LEFT — Promo content ──────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, x: -32 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.65, ease: [0.23, 1, 0.32, 1] }}
        style={{
          position: 'relative',
          zIndex: 2,
          flex: '0 0 52%',
          height: '100vh',
          padding: '64px 72px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          gap: 24,
        }}
      >
        <p style={{
          color: 'rgba(255,255,255,0.3)',
          fontSize: 10,
          letterSpacing: '0.24em',
          textTransform: 'uppercase',
        }}>
          Your account
        </p>

        <h2 style={{
          color: '#ffffff',
          fontSize: 'clamp(64px, 6.5vw, 96px)',
          fontWeight: 600,
          lineHeight: 1.05,
          letterSpacing: '-0.03em',
        }}>
          Book once. Access everything.
        </h2>

        <p style={{
          color: 'rgba(255,255,255,0.38)',
          fontSize: 15,
          lineHeight: 1.7,
          letterSpacing: '0.01em',
        }}>
          Fill in your details and we'll create your personal portal — where all your bookings, payments, and support live.
        </p>
      </motion.div>

      {/* ── RIGHT — Glass form card ───────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, x: 32 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.65, ease: [0.23, 1, 0.32, 1] }}
        style={{
          position: 'relative',
          zIndex: 2,
          flex: '0 0 48%',
          height: '100vh',
          padding: '64px 72px 64px 48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Glass card */}
        <div style={{
          width: '100%',
          maxWidth: 480,
          background: 'rgba(255,255,255,0.04)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          border: '1px solid rgba(255,255,255,0.09)',
          borderRadius: 20,
          padding: '48px 40px',
        }}>
          <p style={{
            color: 'rgba(255,255,255,0.22)',
            fontSize: 10,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            marginBottom: 36,
          }}>
            Your details
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Name row */}
            <div style={{ display: 'flex', gap: 14 }}>
              <FormField
                label="First name"
                name="firstName"
                value={customerInfo.firstName}
                error={touched.firstName ? errors.firstName : ''}
                onChange={handleChange}
                onBlur={() => handleBlur('firstName')}
                placeholder="Alex"
              />
              <FormField
                label="Last name"
                name="lastName"
                value={customerInfo.lastName}
                error={touched.lastName ? errors.lastName : ''}
                onChange={handleChange}
                onBlur={() => handleBlur('lastName')}
                placeholder="Smith"
              />
            </div>

            {/* Email */}
            {requireEmail && (
              <FormField
                label="Email"
                name="email"
                type="email"
                value={customerInfo.email}
                error={touched.email ? errors.email : ''}
                onChange={handleChange}
                onBlur={() => handleBlur('email')}
                placeholder="alex@example.com"
              />
            )}

            {/* Phone */}
            {requirePhone && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{
                  color: 'rgba(255,255,255,0.25)',
                  fontSize: 10,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                }}>
                  Phone
                </label>
                <div
                  className="lp-imm-phone"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: `1px solid ${
                      touched.phone && errors.phone
                        ? 'rgba(255,90,90,0.45)'
                        : customerInfo.phone && !errors.phone
                          ? 'rgba(255,255,255,0.38)'
                          : 'rgba(255,255,255,0.1)'
                    }`,
                    borderRadius: 10,
                    transition: 'border-color 0.2s',
                    overflow: 'visible',
                  }}
                >
                  <PhoneInput
                    country="us"
                    enableSearch
                    value={customerInfo.phone}
                    onChange={handlePhoneChange}
                    onBlur={() => handleBlur('phone')}
                    inputStyle={{ background: 'transparent', color: '#fff', border: 'none', height: 52, fontSize: 15, width: '100%' }}
                    buttonStyle={{ background: 'transparent', border: 'none' }}
                    containerStyle={{ width: '100%' }}
                    dropdownStyle={{ zIndex: 9999 }}
                    countryCodeEditable={false}
                    placeholder="+1 555 000 0000"
                  />
                </div>
                <AnimatePresence>
                  {touched.phone && errors.phone && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      style={{ color: 'rgba(255,90,90,0.8)', fontSize: 11, letterSpacing: '0.04em', margin: 0 }}
                    >
                      {errors.phone}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Continue */}
          <motion.button
            onClick={canProceed ? onNext : undefined}
            whileHover={canProceed ? { background: 'rgba(255,255,255,0.14)', scale: 1.02 } : {}}
            whileTap={canProceed ? { scale: 0.98 } : {}}
            style={{
              marginTop: 36,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: '14px 24px',
              background: canProceed ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.03)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: `1px solid ${canProceed ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.06)'}`,
              borderRadius: 2,
              color: canProceed ? '#fff' : 'rgba(255,255,255,0.2)',
              fontSize: 13,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              cursor: canProceed ? 'pointer' : 'default',
              transition: 'all 0.25s ease',
            }}
          >
            Continue
            <span style={{ fontSize: 16, color: canProceed ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.12)' }}>→</span>
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

// ── Form field ──────────────────────────────────────────────────────────────

interface FormFieldProps {
  label: string;
  name: string;
  value: string;
  error: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  placeholder?: string;
  type?: string;
}

const FormField: React.FC<FormFieldProps> = ({ label, name, value, error, onChange, onBlur, placeholder, type = 'text' }) => {
  const [focused, setFocused] = React.useState(false);

  const borderColor = error
    ? 'rgba(255,90,90,0.45)'
    : value && !error
      ? focused ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.38)'
      : focused ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.1)';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
        {label}
      </label>
      <input
        className="lp-imm-input"
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); onBlur(); }}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '13px 16px',
          background: 'rgba(255,255,255,0.05)',
          border: `1px solid ${borderColor}`,
          borderRadius: 10,
          color: '#fff',
          fontSize: 15,
          letterSpacing: '0.01em',
          transition: 'border-color 0.2s, background 0.2s',
          boxSizing: 'border-box',
        }}
      />
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ color: 'rgba(255,90,90,0.8)', fontSize: 11, letterSpacing: '0.04em', margin: 0 }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImmersiveCustomerInfo;
